// The explorer's address lists, in one registry.
//
// Each list is a page under /btc-explorer. They exist for search as much as for
// browsing: "binance cold wallet address", "bitcoin mining pool addresses" and
// the like are real recurring queries with weak answers, and a page of written
// notes serves them where a live transaction tape cannot.
//
// Adding one means adding a routeMeta entry and a row here. Slugs must be
// lowercase: these are prerendered as directories, and Netlify's pretty-URL
// redirect lowercases the path.
//
// routeMeta.js is deliberately left importing nothing - scripts/prerender.mjs
// loads it by copying it to a temp file, which breaks any relative import - so
// the copy lives there and this file reads it, rather than the other way round.

import { curatedWallets, BALANCES_CHECKED } from "./curatedWalletData";
import { routeMeta } from "./routeMeta";
import {
  miningPools,
  MINING_POOLS_AS_OF,
  MINING_POOLS_SAMPLE,
} from "./miningPools";

// Entries are normalised to { address, label, note, aside } so one component can
// render any list.
const fromCurated = (category) => () =>
  curatedWallets
    .filter((wallet) => wallet.category === category)
    .map((wallet) => ({
      address: wallet.address,
      label: wallet.label,
      note: wallet.note,
      aside: `~${wallet.approxBtc.toLocaleString()} BTC`,
    }));

const fromMiningPools = () =>
  miningPools.map((pool) => ({
    address: pool.address,
    label: pool.pool,
    note: `Received the block reward at this address ${pool.blocks} times in the last ${MINING_POOLS_SAMPLE.toLocaleString()} blocks.`,
    aside: `${pool.share}% of blocks`,
  }));

// Said on every page, because the two kinds of claim are not the same and the
// difference is the whole reason to trust either.
// The date goes in the caveat, so it reaches the served HTML and keeps. How long
// ago that was is added by the page at render time - see src/dataAge.js for why
// a calculation must not be baked into a file that outlives its own deploy.
const CURATED_CAVEAT =
  "Labels are attributions from public explorer tags, reporting and, where a note says so, the owner's own published list — not proof of ownership. " +
  `Balances are approximate, recorded ${BALANCES_CHECKED}.`;

export const walletLists = [
  {
    slug: "exchanges",
    ...routeMeta["/btc-explorer/exchanges"],
    entries: fromCurated("Exchange"),
    caveat: CURATED_CAVEAT,
    checked: BALANCES_CHECKED,
  },
  {
    slug: "government",
    ...routeMeta["/btc-explorer/government"],
    entries: fromCurated("Government"),
    caveat: CURATED_CAVEAT,
    checked: BALANCES_CHECKED,
  },
  {
    slug: "whales",
    ...routeMeta["/btc-explorer/whales"],
    entries: fromCurated("Whales"),
    caveat: CURATED_CAVEAT,
    checked: BALANCES_CHECKED,
  },
  {
    slug: "historic",
    ...routeMeta["/btc-explorer/historic"],
    entries: fromCurated("Historic"),
    caveat: CURATED_CAVEAT,
    checked: BALANCES_CHECKED,
  },
  {
    slug: "mining-pools",
    ...routeMeta["/btc-explorer/mining-pools"],
    entries: fromMiningPools,
    caveat: `Not attributions. The block reward arrived at each of these addresses and the blocks are public, so every entry carries its own evidence. Sampled ${MINING_POOLS_SAMPLE.toLocaleString()} blocks on ${MINING_POOLS_AS_OF}; pools do rotate addresses.`,
    // tighter than the default: pools rotate addresses faster than an exchange
    // relabels a cold wallet, so this list goes off sooner
    checked: MINING_POOLS_AS_OF,
    staleAfter: 120,
  },
];

export const findWalletList = (slug) =>
  walletLists.find((list) => list.slug === slug);

// The sanctions page is a list too, but it groups by designated party rather
// than listing addresses flat, so it has its own component. It appears in the
// directory on the explorer landing alongside these.
export const walletListPaths = walletLists.map(
  (list) => `/btc-explorer/${list.slug}`
);
